import { QueryButtonTag } from "components/Button/QueryButtonTag/QueryButtonTag";
import { SearchMode } from "helpers/api";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useSearchMode from "hooks/useSearchMode";
import { useThread } from "hooks/useThread/useThread";
import { useRouter } from "next/router";
import { useCallback } from "react";

/**
 * Supported mode types - matches naming from markdown
 */
type ModeType = "quick" | "pro" | "deep" | "";

/**
 * Props for the OpenResearchQuestion component
 */
type OpenResearchQuestionProps = {
  /** The research question text to display and search for */
  question: string;
  /** Optional thread ID for adding the question to an existing conversation thread */
  threadId?: string;
  /** Optional mode to set before executing the search */
  mode?: ModeType;
};

/**
 * Mapping from mode prop to SearchMode enum
 */
const MODE_TO_SEARCH_MODE: Record<ModeType, SearchMode | null> = {
  quick: SearchMode.SUMMARY,
  pro: SearchMode.PRO_ANALYSIS,
  deep: SearchMode.PRO_RESEARCH,
  "": null, // Empty string means use current mode
};

/**
 * A clickable button component that represents an open research question or
 * follow-up query. When clicked, it either starts a new search or adds the
 * question to an existing conversation thread.
 *
 * Features:
 * - Displays as a styled query button with follow-up icon
 * - Supports both new searches and thread interactions
 * - Integrates with analytics tracking via Mixpanel
 * - Can change search mode before executing if mode prop is provided
 * - Handles text wrapping for longer questions
 *
 */
function OpenResearchQuestion({
  question,
  threadId,
  mode = "",
}: OpenResearchQuestionProps) {
  const router = useRouter();
  const { addInteractionToThread } = useThread(threadId);
  const { currentSearchMode, changeSearchMode } = useSearchMode();

  // Determine which search mode to use
  const targetSearchMode = mode ? MODE_TO_SEARCH_MODE[mode] : null;
  const searchModeToUse = targetSearchMode || currentSearchMode;

  /**
   * Handles the click event for the research question button.
   * Changes search mode if needed, then executes the search.
   */
  const handleClick = useCallback(async () => {
    // Change search mode if a specific mode was requested
    if (targetSearchMode && targetSearchMode !== currentSearchMode) {
      await changeSearchMode(targetSearchMode);
    }

    if (threadId && addInteractionToThread) {
      addInteractionToThread({
        userMessage: question,
        filters: {}, // Use empty filters for research questions
        searchMode: searchModeToUse,
        actionSource: MixpanelActionSource.ThreadInteractionResults,
      });
    } else {
      router.push(`/search?q=${encodeURIComponent(question)}`);
    }
  }, [
    question,
    router,
    threadId,
    addInteractionToThread,
    searchModeToUse,
    targetSearchMode,
    currentSearchMode,
    changeSearchMode,
  ]);

  return (
    <QueryButtonTag
      icon="follow-up"
      onClick={handleClick}
      q={question}
      allowTextWrap={true}
    />
  );
}

/**
 * Placeholder component shown while research question data is loading
 */
export const OpenResearchQuestionPlaceholder = () => {
  return <QueryButtonTag loading={true} icon="follow-up" />;
};

/**
 * Converts research question data to markdown format for plain text export
 *
 * @param props - OpenResearchQuestion props containing the question text and mode
 * @returns Formatted markdown string with bold question text
 */
export const OpenResearchQuestionMarkdown = ({
  question,
  mode,
}: OpenResearchQuestionProps) => {
  return `**${question}**`;
};

export default OpenResearchQuestion;
